import React from 'react';
import {
  AtomicBlockUtils,
  ContentState,
  convertToRaw,
  DraftHandleValue,
  EditorState,
  SelectionState,
} from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { Editor } from 'react-draft-wysiwyg';
import { toast } from 'react-toastify';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { downscaleImage } from './downscaleImage';

/** Lets the editor take pasted, dropped and uploaded images inline, as data
    URIs in the HTML it emits. */
export interface EditorImages {
  maxBytes: number;
  /** MIME types, e.g. image/png. */
  types: string[];
}

const readAsDataUrl = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// A caret at the selection's focus, or null if its block is gone. Images go in
// at a point, never over a range: by the time one is processed the selection
// may be text the rep chose since, and inserting over it would delete it.
const caretAt = (
  content: ContentState,
  selection: SelectionState,
): SelectionState | null => {
  const key = selection.getFocusKey();
  const block = content.getBlockForKey(key);
  if (!block) return null;
  const offset = Math.min(selection.getFocusOffset(), block.getLength());
  return SelectionState.createEmpty(key).merge({
    anchorOffset: offset,
    focusOffset: offset,
  }) as SelectionState;
};

const WYSIWYGEditor = ({
  onChange,
  value,
  placeholder,
  editorStyle,
  wrapperClassName,
  readOnly,
  ariaLabel,
  images,
  onImagesPending,
}: {
  onChange: any;
  value: any;
  placeholder?: string;
  editorStyle?: any;
  wrapperClassName?: any;
  /** Locks the editor while a send is in flight, so what was submitted is
      what gets cleared. */
  readOnly?: boolean;
  ariaLabel?: string;
  images?: EditorImages;
  /** True while a pasted, dropped or uploaded image is still being processed,
      so the form can hold Send until it lands. */
  onImagesPending?: (pending: boolean) => void;
}) => {
  const [editorState, setEditorState] = React.useState(
    EditorState.createEmpty(),
  );
  const [updated, setUpdated] = React.useState(false);
  // Reading a file is async, so an image lands on whatever the rep has typed
  // since, not on the state the paste started from.
  const latest = React.useRef(editorState);
  latest.current = editorState;
  // An image still processing when the editor goes away (back, another triage,
  // a sent reply's fresh composer) must not land anywhere or call onChange.
  const alive = React.useRef(true);
  const pending = React.useRef(0);
  const reportPending = React.useRef(onImagesPending);
  reportPending.current = onImagesPending;

  React.useEffect(
    () => () => {
      alive.current = false;
      if (pending.current) reportPending.current?.(false);
    },
    [],
  );

  const tracked = async <T,>(work: () => Promise<T>): Promise<T> => {
    pending.current += 1;
    if (pending.current === 1) reportPending.current?.(true);
    try {
      return await work();
    } finally {
      pending.current -= 1;
      if (alive.current && pending.current === 0) {
        reportPending.current?.(false);
      }
    }
  };

  React.useEffect(() => {
    if (!updated) {
      const defaultValue = value ? value : '';
      const blocksFromHtml = htmlToDraft(defaultValue);
      const contentState = ContentState.createFromBlockArray(
        blocksFromHtml.contentBlocks,
        blocksFromHtml.entityMap,
      );
      const newEditorState = EditorState.createWithContent(contentState);
      setEditorState(newEditorState);
    }
  }, [value]);

  const onEditorStateChange = (editorState: any) => {
    setUpdated(true);
    setEditorState(editorState);
    latest.current = editorState;

    return onChange(draftToHtml(convertToRaw(editorState.getCurrentContent())));
  };

  // Refuses with the reason, so a rep is never left wondering where an image
  // went. The server enforces the same limits.
  const readImage = async (file: Blob): Promise<string> => {
    if (!images) throw new Error('Images are not enabled');
    if (!images.types.includes(file.type)) {
      toast.error('Only PNG, JPEG, GIF or WebP images can be added.');
      throw new Error('Unsupported image type');
    }
    // GIFs keep their animation, so they are only size-checked.
    let image: Blob = file;
    if (file.type !== 'image/gif') {
      try {
        image = await downscaleImage(file);
      } catch (error) {
        toast.error('This image could not be added.');
        throw error;
      }
    }
    if (image.size > images.maxBytes) {
      toast.error(
        `Images must be ${images.maxBytes / (1024 * 1024)} MB or smaller.`,
      );
      throw new Error('Image too large');
    }
    return readAsDataUrl(image);
  };

  // Same entity and atomic block the toolbar's image control inserts, so a
  // pasted image renders and serialises exactly like an uploaded one.
  // `at` is where a drop landed; a paste goes at the caret. Each later image
  // follows the one before it.
  const insertImages = async (
    files: Blob[],
    at?: SelectionState,
  ): Promise<void> => {
    let target = at;
    for (const file of files) {
      let src: string;
      try {
        src = await tracked(() => readImage(file));
      } catch {
        continue;
      }
      if (!alive.current) return;
      const current = latest.current;
      const point =
        (target && caretAt(current.getCurrentContent(), target)) ||
        caretAt(current.getCurrentContent(), current.getSelection());
      if (!point) continue;
      const content = current
        .getCurrentContent()
        .createEntity('IMAGE', 'MUTABLE', {
          src,
          height: 'auto',
          width: 'auto',
          alt: 'Pasted image',
        });
      onEditorStateChange(
        AtomicBlockUtils.insertAtomicBlock(
          EditorState.acceptSelection(current, point),
          content.getLastCreatedEntityKey(),
          ' ',
        ),
      );
      target = undefined;
    }
  };

  const imageProps = images
    ? {
        handlePastedFiles: (files: Blob[]): DraftHandleValue => {
          insertImages(files);
          return 'handled';
        },
        handleDroppedFiles: (
          selection: SelectionState,
          files: Blob[],
        ): DraftHandleValue => {
          insertImages(files, selection);
          return 'handled';
        },
      }
    : {};

  return (
    <>
      <div className="editor">
        <Editor
          spellCheck
          editorState={editorState}
          placeholder={placeholder}
          onEditorStateChange={onEditorStateChange}
          editorStyle={editorStyle}
          wrapperClassName={wrapperClassName}
          readOnly={readOnly}
          ariaLabel={ariaLabel}
          // The toolbar edits the content too, so leaving it live while the
          // editor is locked would let a keystroke-free change slip past.
          toolbarHidden={readOnly}
          handlePastedText={() => false}
          {...imageProps}
          toolbar={{
            // An upload, not a URL: the server drops remote images from
            // outgoing mail, so a URL field would offer something that
            // silently never arrives.
            ...(images && {
              image: {
                uploadEnabled: true,
                urlEnabled: false,
                previewImage: true,
                inputAccept: images.types.join(','),
                alt: { present: false },
                defaultSize: { height: 'auto', width: 'auto' },
                uploadCallback: async (file: Blob) => ({
                  data: { link: await tracked(() => readImage(file)) },
                }),
              },
            }),
            options: [
              'inline',
              'blockType',
              'fontSize',
              'list',
              'textAlign',
              'colorPicker',
              'link',
              'embedded',
              'emoji',
              'image',
              'remove',
              'history',
            ],
          }}
        />
      </div>
    </>
  );
};

export default WYSIWYGEditor;
